import { useCallback } from 'react';
import { Label, type PageHeadLabelProps } from '@direct-frontend/components';
import { type OpenApiSchemaProductVariant } from '#shared/api/schema';
import { type CharacteristicPartial } from '#shared/lib/characteristics';
import { api, ProductVariantTag } from '../../api/api';
import { type ProductVariantMenuOptions, useProductVariantMenu } from '../../hooks/useProductVariantMenu';
import { getProductVariantStatusLabelProps } from '../../lib/status';
import { HeaderCaption } from '../ProductVariantHeaderCaption';
import { ProductVariantHeaderView } from '../ProductVariantHeaderView';

export type ProductVariantHeaderProps = {
    name: string;
    status: OpenApiSchemaProductVariant['status'];
    isNew: boolean;
    variant?: Pick<
        OpenApiSchemaProductVariant,
        | 'id'
        | 'product_id'
        | 'link_url'
        | 'price'
        | 'stocks'
        | 'minimal_price'
        | 'seller_sku'
        | 'kit_id'
        | 'external_ids'
    >;
    characteristics: CharacteristicPartial[];
} & Pick<ProductVariantMenuOptions, 'onStatusChanged' | 'onDeleted'>;

export const ProductVariantHeader = ({
    name,
    status,
    isNew,
    variant,
    characteristics,
    onStatusChanged,
    onDeleted,
}: ProductVariantHeaderProps) => {
    const handleDeleted = useCallback(() => {
        onDeleted?.();

        if (variant) {
            api.util.invalidateTags([{ type: ProductVariantTag, id: variant.id }]);
        }
    }, [onDeleted, variant]);
    const { items, renderConfirmModal } = useProductVariantMenu(variant ? { ...variant, status } : undefined, {
        withEditLink: false,
        onStatusChanged,
        onDeleted: handleDeleted,
    });
    const labels = useCallback(
        ({ labelSize }: PageHeadLabelProps) => {
            const { addonLeft, children } = getProductVariantStatusLabelProps(status);

            return (
                <Label size={labelSize} addonLeft={addonLeft}>
                    {children}
                </Label>
            );
        },
        [status]
    );

    return (
        <ProductVariantHeaderView
            name={name}
            isNew={isNew}
            caption={
                <HeaderCaption
                    sellerSku={variant?.seller_sku}
                    kitId={variant?.kit_id}
                    characteristics={characteristics}
                    externalSystems={variant?.external_ids ?? []}
                    alignItems="flex-start"
                />
            }
            labels={labels}
            menuItems={items}
            additionalContent={renderConfirmModal()}
        />
    );
};
